import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';

// Models and services
import { ToastMessageService } from '@fitness-force/api';
import { CommissionPlanSlabResponse, CommissionSlabDimensionValueResponse, ListCommissionPlanSlabsResponse, CommissionPlanRuleResponse, OwnershipLevel } from '../../models';
import { CommissionPlanSlabService, CommissionPlanRuleService } from '../../services';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';

@Component({
  selector: 'lib-commission-plan-slabs-list',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    ButtonModule,
    ToastModule,
    TagModule,
    TableModule,
    SkeletonModule,
    BreadcrumbComponent,
  ],
  template: `
  <div class="pt-3 md:px-4 px-2 pb-8 safe-area-margin">
    <h1 class="text-base md:text-lg text-900 flex align-items-center mb-0 line-height-1">
      {{ 'COMMISSION_MANAGEMENT.SLABS.TITLE' | translate }}
    </h1>
    <div class="mt-1 mb-3">
      <app-breadcrumb />
    </div>

    <div class="surface-0 border-round shadow-1 mt-3">
      <div class="px-2 md:px-3 pt-3 pb-3">
        <div class="flex justify-content-between align-items-center mb-3">
          <div>
            @if (ruleName) {
              <p class="text-500 mt-0 mb-0">{{ 'COMMISSION_MANAGEMENT.SLABS.SUBTITLE' | translate: {ruleName: ruleName} }}</p>
            }
          </div>
          <div class="flex gap-2">
            <p-button
              [label]="'COMMISSION_MANAGEMENT.COMMON.BACK' | translate"
              icon="pi pi-arrow-left"
              [text]="true"
              (onClick)="goBack()"
              aria-label="back-button" />

          </div>
        </div>

        <!-- Skeleton loader -->
        @if (loading) {
          <div class="flex flex-column gap-2">
            @for (i of [1, 2, 3]; track i) {
              <p-skeleton height="2.5rem" styleClass="w-full border-round" />
            }
          </div>
        }

        <!-- Slabs table -->
        @if (!loading) {
          <p-table
            [value]="slabs"
            [scrollable]="true"
            scrollHeight="flex"
            styleClass="p-datatable-sm p-datatable-gridlines"
            aria-label="commission-plan-slabs-table"
            data-testid="commission-plan-slabs-data-grid">

            <ng-template pTemplate="header">
              <tr>
                <!-- Frozen: Qty range -->
                <th
                  pFrozenColumn
                  alignFrozen="left"
                  class="text-center font-semibold text-900 text-sm surface-100 white-space-nowrap"
                  style="min-width: 150px;">
                  {{ 'COMMISSION_MANAGEMENT.SLABS.QUANTITY_FROM' | translate }}
                  &ndash;
                  {{ 'COMMISSION_MANAGEMENT.SLABS.QUANTITY_TO' | translate }}
                </th>
                <!-- Frozen: Revenue range -->
                <th
                  pFrozenColumn
                  alignFrozen="left"
                  class="text-center font-semibold text-900 text-sm surface-100 white-space-nowrap"
                  style="min-width: 160px;">
                  {{ 'COMMISSION_MANAGEMENT.SLABS.REVENUE_FROM' | translate }}
                  &ndash;
                  {{ 'COMMISSION_MANAGEMENT.SLABS.REVENUE_TO' | translate }}
                </th>
                <!-- Scrollable: one column per dimension from values[] -->
                @for (dim of dimensionColumns; track dim) {
                  <th
                    class="text-center font-semibold text-900 text-sm surface-100 white-space-nowrap"
                    style="min-width: 150px;">
                    {{ dim }}
                  </th>
                }
                <!-- Frozen right: Status -->
                <th
                  pFrozenColumn
                  alignFrozen="right"
                  class="text-center font-semibold text-900 text-sm surface-100 white-space-nowrap"
                  style="min-width: 100px;">
                  {{ 'COMMISSION_MANAGEMENT.SLABS.STATUS' | translate }}
                </th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-slab let-i="rowIndex">
              <tr>
                <!-- Frozen left: Qty range -->
                <td pFrozenColumn alignFrozen="left" class="text-center white-space-nowrap surface-0">
                  @if (slab.quantity_from != null || slab.quantity_to != null) {
                    <span>{{ slab.quantity_from ?? '–' }}</span>
                    <span class="mx-1 text-400">–</span>
                    <span>{{ slab.quantity_to ?? '–' }}</span>
                  } @else {
                    <span class="text-400">–</span>
                  }
                </td>
                <!-- Frozen left: Revenue range -->
                <td pFrozenColumn alignFrozen="left" class="text-center white-space-nowrap surface-0">
                  @if (slab.revenue_from != null || slab.revenue_to != null) {
                    <span>{{ slab.revenue_from ?? '–' }}</span>
                    <span class="mx-1 text-400">–</span>
                    <span>{{ slab.revenue_to ?? '–' }}</span>
                  } @else {
                    <span class="text-400">–</span>
                  }
                </td>
                <!-- Scrollable: dimension values -->
                @for (dim of dimensionColumns; track dim) {
                  <td class="text-center white-space-nowrap">
                    {{ getDimensionDisplay(slab, dim) }}
                  </td>
                }
                <!-- Frozen right: Status -->
                <td pFrozenColumn alignFrozen="right" class="text-center surface-0">
                  <p-tag
                    [value]="slab.is_active
                      ? ('ADMIN.COMMON.ACTIVE' | translate)
                      : ('ADMIN.COMMON.INACTIVE' | translate)"
                    [severity]="slab.is_active ? 'success' : 'danger'"
                    [attr.aria-label]="'slab-status-' + i" />
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td [attr.colspan]="dimensionColumns.length + 3" class="text-center py-4 text-500">
                  {{ 'COMMISSION_MANAGEMENT.SLABS.NO_SLABS' | translate }}
                </td>
              </tr>
            </ng-template>

          </p-table>
        }
      </div>
    </div>
  </div>
  <p-toast />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommissionPlanSlabsListComponent implements OnInit {
  private readonly commissionPlanSlabService = inject(CommissionPlanSlabService);
  private readonly commissionPlanRuleService = inject(CommissionPlanRuleService);
  private readonly toastService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  slabs: CommissionPlanSlabResponse[] = [];
  loading = false;
  planId!: number;
  ruleId!: number;
  ownership!: string;
  ruleName = '';

  /** Unique dimensionId values across all slabs — become scrollable columns */
  dimensionColumns: string[] = [];

  ngOnInit() {
    this.route.params.subscribe(params => {
      if (params['planId'] && params['ruleId']) {
        this.planId = +params['planId'];
        this.ruleId = +params['ruleId'];
        const ownershipParam = params['ownership'] as OwnershipLevel;
        this.ownership = ownershipParam && Object.values(OwnershipLevel).includes(ownershipParam)
          ? ownershipParam
          : OwnershipLevel.Company;
        this.loadRule();
        this.loadSlabs();
      }
    });
  }

  loadRule() {
    this.commissionPlanRuleService.getCommissionPlanRule(this.planId, this.ruleId).subscribe({
      next: (rule: CommissionPlanRuleResponse) => {
        const category = rule.category
          ? this.translate.instant(`COMMISSION_MANAGEMENT.CATEGORIES.${rule.category.toUpperCase()}`)
          : '';
        const salesBasis = rule.sales_basis
          ? this.translate.instant(`COMMISSION_MANAGEMENT.SALES_BASIS.${rule.sales_basis.toUpperCase()}`)
          : '';
        this.ruleName = `${category} - ${salesBasis}`;
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        this.ruleName = `Rule ${this.ruleId}`;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  loadSlabs() {
    this.loading = true;
    this.commissionPlanSlabService.getCommissionPlanSlabs(this.planId, this.ruleId).subscribe({
      next: (response: ListCommissionPlanSlabsResponse) => {
        this.slabs = response.items || [];
        this.dimensionColumns = this.extractDimensionColumns(this.slabs);
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      },
      error: (error) => {
        this.toastService.showError(error);
        this.slabs = [];
        this.dimensionColumns = [];
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  /** Collect unique dimensionId strings in the order they first appear across all slabs */
  private extractDimensionColumns(slabs: CommissionPlanSlabResponse[]): string[] {
    const seen = new Set<string>();
    const cols: string[] = [];
    for (const slab of slabs) {
      for (const v of slab.values ?? []) {
        if (v.dimensionId && !seen.has(v.dimensionId)) {
          seen.add(v.dimensionId);
          cols.push(v.dimensionId);
        }
      }
    }
    return cols;
  }

  /** Format a dimension value for display: percentage or flat amount */
  getDimensionDisplay(slab: CommissionPlanSlabResponse, dimensionId: string): string {
    const val: CommissionSlabDimensionValueResponse | undefined =
      (slab.values ?? []).find(v => v.dimensionId === dimensionId);
    if (!val) return '–';
    if (val.percentage != null) return `${val.percentage}%`;
    if (val.amountMinorCurr != null) return (val.amountMinorCurr / 100).toFixed(2);
    return '–';
  }

  createSlab() {
    this.router.navigate([
      '/admin/commission-management/plans',
      this.ownership,
      this.planId,
      'rules',
      this.ruleId,
      'slabs',
      'create'
    ]);
  }

  goBack() {
    this.router.navigate([
      '/admin/commission-management/plans',
      this.ownership,
      this.planId,
      'rules'
    ]);
  }
}